import { FC, useCallback, useMemo, useState } from 'react';
import Page, { PageProps } from '@/components/page';
import { Stack } from '@mui/material';
import DataTable from '@/components/data-table';
import PageHeader from '@/components/page/header';
import { DtColumn, DtFilter, DtFilterValue, DtPage, DtSort } from '@/components/data-table/helpers';
import Text from '@/components/text';
import { searchUsers } from '@/lib/services/user.service';
import { QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';
import useFeature from '@/hooks/use-feature.hook';
import useToast from '@/hooks/use-toast.hook';
import { getDaysAgoDate } from '@/lib/helpers';
import { getUrlQueryParam } from '@/lib/url-helpers';
import {
  downloadAuditLogs,
  getAuditLogs,
  getAuditLogSettings,
} from '@/lib/services/audit-log.service';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import NoneChip from '@/components/chip/none-chip';
import NiceDate from '@/components/nice-date';
import { AuditLogSettingsModel } from '@/lib/models/audit-log/audit-settings.model';
import { useQuery } from 'react-query';
import useQueryHelper from '@/hooks/use-query-helper';
import Chip from '@/components/chip';
import { startCase } from 'lodash';
import PageBlurb from '@/components/page/blurb';
import useDrawer from '@/hooks/use-drawer.hook';
import CopyClipButton from '@/components/copy-clip-button';

const columns: DtColumn[] = [
  { name: 'eventDateTime', label: 'Date', flex: 0.6, sortable: false },
  { name: 'actor', label: 'Actor', sortable: false },
  { name: 'type', label: 'Type', sortable: false },
  { name: 'action', label: 'Action', flex: 0.6, sortable: false },
  { name: 'message', label: 'Details', flex: 2, sortable: false },
  { name: 'resource', label: 'Resource', sortable: false },
  { name: 'actions', label: '', flex: 0.2, sortable: false },
];

const AuditLogsPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');
  const { loading: featuresLoading } = useFeature();
  const { errorToast } = useToast();

  const query = useQuery([QueryKey.AuditLogSettings], async () => getAuditLogSettings());
  const { showLoader: settingsLoading } = useQueryHelper(query);
  const { data = new AuditLogSettingsModel() } = query;

  const compFilter = useMemo(() => {
    const { allActionsOptions, allCategoriesOptions } = data!;
    const resourceIdFromUrl = getUrlQueryParam('resourceId');

    const initialValues: DtFilterValue[] = [];
    if (resourceIdFromUrl) {
      initialValues.push({
        columnName: 'resourceId',
        value: resourceIdFromUrl,
      });
    }

    return {
      keyword: '',
      fields: [
        {
          columnName: 'date',
          label: 'Date',
          control: 'date-range' as const,
          minDate: () => getDaysAgoDate(30),
          maxDate: () => new Date(),
        },
        {
          columnName: 'categories',
          label: 'Type',
          control: 'select-multi' as const,
          highlander: true,
          options: allCategoriesOptions || [],
        },
        {
          columnName: 'actions',
          label: 'Action',
          control: 'select-multi' as const,
          highlander: true,
          options: allActionsOptions || [],
        },
        {
          columnName: 'actor',
          label: 'Actor',
          searchLabel: 'Search by name or email',
          control: 'select-search' as const,
          onSearch: async (value: string) => {
            // Allow searching for "System" as if they were searching for a user
            const users = value ? await searchUsers(value) : [];
            const includeSystem = 'system'.includes(value.toLowerCase());
            return [
              ...users.map(({ name, email, id }) => ({
                label: name && email ? `${name} <${email}>` : name || email,
                value: id,
              })),
              ...(includeSystem ? [{ label: 'System', value: 'system' }] : []),
            ];
          },
        },
        {
          columnName: 'resourceId',
          label: 'Resource ID',
          control: 'text-hexadecimal' as const,
        },
      ],
      values: initialValues,
    };
  }, [data]);

  const handleView = useCallback(
    (id: string) => {
      setSelectedRowId(id);
      openDrawer('audit-log', {
        id,
        onClose: () => {
          setSelectedRowId('');
        },
      });
    },
    [openDrawer]
  );

  const handleExport = async (
    _page: number,
    _pageSize: number,
    _sort?: DtSort,
    filter?: DtFilter
  ) => {
    return downloadAuditLogs(filter).then((downloaded) => {
      if (!downloaded) {
        errorToast('Failed to generate download');
      }
    });
  };

  const handleChangeFilter = (filterValues: DtFilterValue[]) => {
    return filterValues;
  };

  const loadData = useCallback(
    async (pageParams: DtPage, filter?: DtFilter) => {
      const { page, pageSize, sort } = pageParams;
      const result = await getAuditLogs(
        page,
        pageSize,
        sort || { columnName: 'startTime', direction: 'desc' },
        filter
      );

      return {
        page,
        pageSize,
        sort,
        filter,
        rows: result.rows.map(
          ({
            id,
            eventDateTime,
            eventType,
            actionLabel,
            actionColor,
            cleanMessage,
            actor,
            resourceLabel,
          }) => {
            const { name, email } = actor;

            const DateEl = (
              <Stack>
                <NiceDate size="x-small" val={eventDateTime} withTime />
              </Stack>
            );
            const TypeEl = <Text size="small">{startCase(eventType)}</Text>;
            const ActionEl = (
              <Chip label={actionLabel} color={actionColor || 'info'} size="small" />
            );
            const MessageEl = <Text size="small">{cleanMessage}</Text>;

            const NameEl = email ? (
              <Stack maxWidth="100%">
                {name && (
                  <Text size="small" dotdot>
                    {name}
                  </Text>
                )}
                {email && (
                  <Text size="small" color="grey" dotdot>
                    {email}
                  </Text>
                )}
              </Stack>
            ) : (
              <NoneChip system />
            );

            const ActionsEl = <OpenDrawerButton onClick={() => handleView(id)} />;

            const ResourceEl = resourceLabel ? (
              <Stack direction="row" gap={0.5}>
                <CopyClipButton
                  value={resourceLabel}
                  tooltip="Copy Resource ID"
                  type="resource-id"
                />
                <Text size="small">{resourceLabel}</Text>
              </Stack>
            ) : (
              <NoneChip notAvailable />
            );

            return {
              id,
              eventDateTime: DateEl,
              type: TypeEl,
              action: ActionEl,
              message: MessageEl,
              actor: NameEl,
              actions: ActionsEl,
              resource: ResourceEl,
            };
          }
        ),
        total: result.total,
      };
    },
    [handleView]
  );

  if (featuresLoading || settingsLoading) {
    return null;
  }

  return (
    <Page title="Audit Logs">
      <PageHeader>
        <PageBlurb>
          View audit history of system, admin, and user events for the past 30 days.
        </PageBlurb>
      </PageHeader>
      <DomSize>
        <DataTable
          queryKey={QueryKey.AuditLogsDataset}
          searchPlaceholder="Search details"
          columns={columns}
          onLoad={loadData}
          onChangeFilter={handleChangeFilter}
          rowHeight={70}
          filter={compFilter}
          search
          poll={15000}
          selectedRowId={selectedRowId}
          onExport={handleExport}
        />
      </DomSize>
    </Page>
  );
};

export default AuditLogsPage;
